import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ValidationError } from '../models/User';
import { AuthentificationApi, UsersApi, Configuration } from '../api/services';
import { useAuth } from '../contexts/AuthContext';

export interface UserProfileFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  isLoading: boolean;
  errors: ValidationError[];
  success: boolean;
  message: string;
}

export class UserProfileViewModel {
  private navigate: ReturnType<typeof useNavigate>;
  private authApi: AuthentificationApi;
  private usersApi: UsersApi;

  constructor(navigate: ReturnType<typeof useNavigate>) {
    this.navigate = navigate;
    this.authApi = new AuthentificationApi();
    this.usersApi = new UsersApi();
  }

  // Initial state
  getInitialState(): UserProfileFormState {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      birthDate: '',
      isLoading: false,
      errors: [],
      success: false,
      message: ''
    };
  }

  // Load user data
  async loadUserProfile(userId: string, token: string): Promise<{ success: boolean; user?: User; message?: string }> {
    try {
      console.log('🔧 Configuration API avec:', { 
        basePath: process.env.REACT_APP_API_URL, 
        userId, 
        hasToken: !!token 
      });
      
      // Test direct avec fetch pour voir si l'API fonctionne
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/users/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Réponse fetch:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const userData = await response.json();
      console.log('📥 Données utilisateur:', userData);

      return {
        success: true,
        user: userData
      };
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement du profil:', error);
      return {
        success: false,
        message: `Erreur lors du chargement du profil: ${error.message}`
      };
    }
  }

  // Update user profile
  async updateUserProfile(userId: string, userData: any, token: string): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    try {
      const validationErrors = this.validateForm(userData);
      if (validationErrors.length > 0) {
        return {
          success: false,
          errors: validationErrors.map(e => e.message)
        };
      }

      console.log('📡 Appel API pour mettre à jour le profil...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Profil mis à jour avec succès:', result);

      return {
        success: true,
        message: 'Profil mis à jour avec succès !'
      };

    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      if (error.response?.data?.message) {
        return {
          success: false,
          message: error.response.data.message
        };
      }
      return {
        success: false,
        message: 'Une erreur est survenue lors de la mise à jour'
      };
    }
  }

  // Client-side validation
  validateForm(userData: any): ValidationError[] {
    const errors: ValidationError[] = [];

    if (userData.first_name && !userData.first_name.trim()) {
      errors.push({ field: 'firstName', message: 'Le prénom est requis' });
    }

    if (userData.last_name && !userData.last_name.trim()) {
      errors.push({ field: 'lastName', message: 'Le nom est requis' });
    }

    if (userData.email && !this.isValidEmail(userData.email)) {
      errors.push({ field: 'email', message: 'Format d\'email invalide' });
    }

    if (userData.phone && userData.phone.trim().length < 8) {
      errors.push({ field: 'phone', message: 'Le numéro de téléphone doit contenir au moins 8 caractères' });
    }

    if (userData.birth_date) {
      const birthDate = new Date(userData.birth_date);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        errors.push({ field: 'birthDate', message: 'Vous devez avoir au moins 18 ans' });
      }
    }

    return errors;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Check if form is valid
  isFormValid(userData: Partial<User>): boolean {
    const errors = this.validateForm(userData);
    return errors.length === 0;
  }
}

// React Hook to use the ViewModel
export const useUserProfileViewModel = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [state, setState] = useState<UserProfileFormState>(new UserProfileViewModel(navigate).getInitialState());

  const viewModel = new UserProfileViewModel(navigate);

  const updateField = useCallback((field: keyof UserProfileFormState, value: string) => {
    setState(prevState => ({
      ...prevState,
      [field]: value,
      errors: []
    }));
  }, []);

  const loadProfile = useCallback(async () => {
    const userAny = user as any;
    console.log('🔍 loadProfile appelé avec:', { userId: userAny?.id_user, hasToken: !!token });
    
    if (!userAny?.id_user || !token) {
      console.log('❌ Pas d\'user ou token manquant');
      return;
    }

    setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: [],
      success: false,
      message: ''
    }));

    console.log('📡 Appel API pour récupérer le profil...');
    const result = await viewModel.loadUserProfile(userAny.id_user.toString(), token);
    console.log('📥 Résultat API:', result);

    if (result.success && result.user) {
      const userData = result.user as any;
      console.log('✅ Données utilisateur récupérées:', userData);
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        birthDate: userData.birth_date ? new Date(userData.birth_date).toISOString().split('T')[0] : ''
      }));
    } else {
      console.log('❌ Erreur lors du chargement:', result.message);
      // Données de test temporaires pour vérifier l'affichage
      console.log('🧪 Utilisation de données de test...');
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        firstName: 'Jean',
        lastName: 'Dupont',
        email: 'jean.dupont@example.com',
        phone: '+216 22 345 678',
        birthDate: '1990-01-15',
        message: result.message || 'Erreur lors du chargement du profil'
      }));
    }
  }, [user?.id, token]);

  const updateProfile = useCallback(async () => {
    const userAny = user as any;
    if (!userAny?.id_user || !token) return { success: false, message: 'Utilisateur non connecté' };

    setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: [],
      success: false,
      message: ''
    }));

    const userData = {
      first_name: state.firstName,
      last_name: state.lastName,
      email: state.email,
      phone: state.phone,
      birth_date: state.birthDate
    };

    const result = await viewModel.updateUserProfile(userAny.id_user.toString(), userData, token);

    if (result.success) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        success: true,
        message: result.message || 'Profil mis à jour avec succès !'
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        errors: result.errors?.map((error: string, index: number) => ({
          field: `field_${index}`,
          message: error
        })) || [],
        message: result.message || 'Erreur lors de la mise à jour'
      }));
    }

    return result;
  }, [state.firstName, state.lastName, state.email, state.phone, state.birthDate, user?.id, token]);

  const isFormValid = useCallback(() => {
    const userData = {
      first_name: state.firstName,
      last_name: state.lastName,
      email: state.email,
      phone: state.phone,
      birth_date: state.birthDate
    };
    return viewModel.isFormValid(userData);
  }, [state.firstName, state.lastName, state.email, state.phone, state.birthDate]);

  return {
    state,
    updateField,
    loadProfile,
    updateProfile,
    isFormValid
  };
}; 